import { useState } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import useMediaQuery from '@mui/material/useMediaQuery'
import Refreshing from 'components/core/Refreshing'
import { fmtCount } from '../StreamIngest/vocabulary'
import { ABOUT_WORDS, PUSH_KINDS, fmtShare, share } from './vocabulary'
import PushFunnel from './PushFunnel'
import PushTrend from './PushTrend'

// The Pushes tab: each kind of push sent in the window, and what it led to
// (reclip-api `stream/metrics/loop/pushes.py`, after the proto console's
// table). Its question is whether a push brings somebody back to hear
// something, and to hear what the push was about.
//
// - **Counted** is the denominator: pushes sent once their person's phone
//   reported anything, or that began a visit it reported. Before a phone
//   reports, a push that led nowhere and a phone that cannot say so look the
//   same, so every share here is of counted pushes, and the line above the
//   table says how many people that leaves.
// - **A visit** is a floor on the taps: the phone reports one only when it
//   showed a quote new to that phone or played one, so a tap onto quotes
//   already seen, or onto a person, leaves nothing. The footnote says so.
// - **What it was about** is the quote for a quote push or a comment, a quote
//   by the friend for friends' new quotes, and nothing for a push about a
//   person: a dash, never a 0%.
//
// How the pushes were delivered (sent, failed, skipped, held past due) is the
// tab's first half (`Delivery.js`); this is what came after.

const COLUMNS = [
  {
    key: 'visited',
    label: 'a visit reported',
    help: 'The tap opened the app and the phone said so. Only visits that showed a new quote or played one are reported, so this is a floor.',
  },
  {
    key: 'heard',
    label: 'led to a listen',
    help: 'Something was heard during the visit the push began.',
  },
  {
    key: 'about',
    label: 'to what it was about',
    help: 'They heard what the push was about: its quote, or a quote by the friend it named.',
  },
]

const Bar = ({ value }) => (
  <Box
    sx={{
      width: 64,
      height: 6,
      borderRadius: 3,
      bgcolor: 'action.hover',
      overflow: 'hidden',
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        width: `${Math.round(Math.min(1, value || 0) * 100)}%`,
        height: '100%',
        bgcolor: 'primary.main',
      }}
    />
  </Box>
)

const Outcome = ({ n, of }) =>
  n == null ? (
    <Typography variant="body2" color="text.disabled">
      —
    </Typography>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Bar value={share(n, of)} />
      <Typography variant="body2" fontWeight={600}>
        {fmtCount(n)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {fmtShare(share(n, of))}
      </Typography>
    </Box>
  )

const aboutOf = (row) =>
  row.about_is ? ABOUT_WORDS[row.about_is] || row.about_is : null

const KindTable = ({ kinds }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: 820 }}>
      <TableHead>
        <TableRow>
          <TableCell>push about</TableCell>
          <TableCell align="right">sent</TableCell>
          <TableCell align="right">
            <Tooltip title="Pushes to phones that report back: every share in the row is out of these.">
              <span>to a reporting phone</span>
            </Tooltip>
          </TableCell>
          {COLUMNS.map(({ key, label, help }) => (
            <TableCell key={key}>
              <Tooltip title={help}>
                <span>{label}</span>
              </Tooltip>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {kinds.map((row) => (
          <TableRow key={row.kind}>
            <TableCell>{PUSH_KINDS[row.kind] || row.kind}</TableCell>
            <TableCell align="right">{fmtCount(row.sent)}</TableCell>
            <TableCell align="right">{fmtCount(row.counted)}</TableCell>
            {COLUMNS.map(({ key }) => (
              <TableCell key={key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Outcome n={row[key]} of={row.counted} />
                  {key === 'about' && aboutOf(row) && (
                    <Typography variant="caption" color="text.secondary">
                      {aboutOf(row)}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
)

// The same rows on a phone: one card per kind, a line per outcome.
const KindCards = ({ kinds }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {kinds.map((row) => (
      <Paper key={row.kind} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {PUSH_KINDS[row.kind] || row.kind}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {fmtCount(row.sent)} sent · {fmtCount(row.counted)} to a reporting
          phone
        </Typography>
        {COLUMNS.map(({ key, label }) => (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              py: 0.25,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {key === 'about' && aboutOf(row) ? `to ${aboutOf(row)}` : label}
            </Typography>
            <Outcome n={row[key]} of={row.counted} />
          </Box>
        ))}
      </Paper>
    ))}
  </Box>
)

// `answer` is the event table's pushes read (`useLoopReads`), `null` until it
// lands; `error` is its refusal, said in full. Drawn as bars (`PushFunnel`),
// with every number in the table view a toggle away.
const Pushes = ({ answer, error, loading }) => {
  const desktop = useMediaQuery((theme) => theme.breakpoints.up('desktop'))
  const [view, setView] = useState('bars')
  const kinds = answer?.kinds || []

  return (
    <Refreshing active={loading}>
      <Paper
        variant="outlined"
        sx={{ p: { mobile: 2, desktop: 3 }, borderRadius: 2, mb: 3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            What each kind of push led to
          </Typography>
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(event, next) => next && setView(next)}
          >
            <ToggleButton value="bars">Bars</ToggleButton>
            <ToggleButton value="table">Table</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {error ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            The event table could not be read: {error}
          </Alert>
        ) : answer ? (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, maxWidth: 900 }}
            >
              {fmtCount(answer.reporting_people)} of the{' '}
              {fmtCount(answer.people)} people pushed{' '}
              {answer.people === 1 ? 'has' : 'have'} a phone that reports
              {answer.first_report_at
                ? `, the first since ${dayjs(answer.first_report_at).format(
                    'MMM D'
                  )}`
                : ''}
              . Every share is of the pushes sent to a phone that reports.
              {answer.read_at
                ? ` Read ${dayjs(answer.read_at).format(
                    'HH:mm'
                  )}, kept five minutes.`
                : ''}
            </Typography>
            {kinds.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No push was sent in this window.
              </Typography>
            ) : view === 'bars' ? (
              <PushFunnel kinds={kinds} />
            ) : desktop ? (
              <KindTable kinds={kinds} />
            ) : (
              <KindCards kinds={kinds} />
            )}
            {kinds.length > 0 && <PushTrend answer={answer} />}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 2 }}
            >
              A visit is reported only when it showed a quote new to that phone
              or played one, so it is a floor on the taps a push got.
              {answer.about_truncated
                ? ' Friends’ new quotes: too many visits to read every owner, so its last column is a floor too.'
                : ''}
            </Typography>
          </>
        ) : null}
      </Paper>
    </Refreshing>
  )
}

export default Pushes
